import { useTheme } from "styled-components";

import { getRefreshBucketStart } from "@lib/logic";
import { type AreaWithSensorInfo, getFloorById, useAppSelector } from "@lib/store";
import { dateToLocalDateTimeString } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useRequiredContext } from "@lib/utils";
import { AvatarStack, ModalCellItemIcon } from "@molecules";

import { HaNContext } from "../shell/HaNContext";
import { AreaBusynessIcon } from "./components/AreaBusynessIcon";
import { useAreaConnections } from "./useAreaConnections";

/** An area in the list and the carousel: its floor, the connections working there now, and how busy its sensors say it is. */
export const AreaCell = ({ area }: { area: AreaWithSensorInfo }) => {
  const theme = useTheme();
  const { bucket } = useRequiredContext(HaNContext);
  const floor = useAppSelector(state => getFloorById(state, area.floorId));

  // The refresh bucket's start, so "now" moves along every 5 minutes while the page is open, with the data
  const now = new Date(getRefreshBucketStart(bucket));
  const nowKey = dateToLocalDateTimeString(now);
  const presentConnections = useAreaConnections({ areaId: area.id, now }).filter(({ reservation }) => {
    return nowKey >= reservation.localStart && nowKey <= reservation.localEnd;
  });

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex min-w-0 items-start gap-3">
        <ModalCellItemIcon
          className="flex-none self-start"
          backgroundColor={theme.card.beige500.background}
          icon="desk"
        />
        <div className="flex min-w-0 flex-col justify-center gap-0.5 pt-0.5">
          <p className="truncate font-semibold">{area.name}</p>
          {floor && <p>{floor.name}</p>}

          <AvatarStack
            date={now.toISOString()}
            users={presentConnections.map(({ user }) => user)}
            overlap
          />
        </div>
      </div>

      <AreaBusynessIcon status={area.sensorInformation?.status ?? "unknown"} />
    </div>
  );
};
